import React, { useEffect, useState, useMemo } from "react";
import "./styles.css";

export const handler = ({ inputs, mechanic }) => {
  const {
    format,
    width,
    height,
    title,
    event1,
    event2,
    event3,
    event4,
    event5,
    event6,
    titleAlign,
    textAlign,
    gap
  } = inputs;

  // Calculate dimensions based on format
  const formatDimensions = {
    vertical: { width: 1080, height: 1350 },
    square: { width: 1080, height: 1080 },
    landscape: { width: 1080, height: 566 },
    story: { width: 1080, height: 1920 },
    custom: { width: width, height: height }
  };
  
  const dimensions = formatDimensions[format];
  const finalWidth = dimensions.width;
  const finalHeight = dimensions.height;

  const [fetchedEvents, setFetchedEvents] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch events from API once on initial load
  useEffect(() => {
    if (!hasFetched) {
      setHasFetched(true);
      
      fetch('https://cms.hetzner.gaswerkareal.sh/api/events')
        .then(res => res.json())
        .then(data => {
          setFetchedEvents(data);
        })
        .catch(err => {
          console.error('Failed to fetch events:', err);
          setFetchedEvents([]);
        });
    }
  }, [hasFetched]);

  // Use useMemo to cache formatted events
  const formattedFetchedEvents = useMemo(() => {
    return fetchedEvents.map(event => {
      const date = new Date(event.date);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const timeStr = event.time ? `${event.time}: ` : '';
      return `${month}.${day}, ${timeStr}${event.title}`;
    });
  }, [fetchedEvents]);

  // Merge manual and fetched events - manual inputs take priority for their slots
  let events = [...formattedFetchedEvents];
  if (event1 && event1.trim()) events[0] = event1;
  if (event2 && event2.trim()) events[1] = event2;
  if (event3 && event3.trim()) events[2] = event3;
  if (event4 && event4.trim()) events[3] = event4;
  if (event5 && event5.trim()) events[4] = event5;
  if (event6 && event6.trim()) events[5] = event6;
  // Limit to 6 events and filter empty
  events = events.slice(0, 6).filter(e => e && e.trim());
  
  // Build event list text
  const displayText = events.map(e => `– ${e}`).join('\n');

  // Generate random rotations on each render
  const finalTitleRotation = parseFloat((Math.random() * 20 - 10).toFixed(1));
  const finalTextRotation = parseFloat((Math.random() * 20 - 10).toFixed(1));

  // Convert alignment to flexbox position
  const getAlignSelf = (align) => {
    if (align === "left") return "flex-start";
    if (align === "right") return "flex-end";
    return "center";
  };

  useEffect(() => {
    mechanic.done();
  }, []);

  return (
    <div
      className="event-layout-container"
      style={{
        width: `${finalWidth}px`,
        height: `${finalHeight}px`,
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: `${gap}px`,
        padding: "60px 40px",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden"
      }}
    >
      <h1
        className="rotated-title"
        style={{
          color: "#6691cb",
          fontSize: "46px",
          fontWeight: 500,
          fontFamily: "Figtree, sans-serif",
          transform: `rotate(${finalTitleRotation}deg)`,
          margin: 0,
          textAlign: titleAlign,
          textTransform: "lowercase"
        }}
      >
        {title}
      </h1>
      
      <p
        className="rotated-text"
        style={{
          color: "#6691cb",
          fontSize: "26px",
          fontWeight: 400,
          fontFamily: "Figtree, sans-serif",
          transform: `rotate(${finalTextRotation}deg)`,
          margin: 0,
          textAlign: "left",
          maxWidth: "80%",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          alignSelf: getAlignSelf(textAlign)
        }}
      >
        {displayText}
      </p>
    </div>
  );
};

export const inputs = {
  format: {
    type: "text",
    default: "vertical",
    options: ["vertical", "square", "landscape", "story", "custom"],
    label: "Format"
  },
  width: {
    type: "number",
    default: 1080,
    label: "Width",
    editable: (inputs) => inputs.format === "custom"
  },
  height: {
    type: "number",
    default: 1350,
    label: "Height",
    editable: (inputs) => inputs.format === "custom"
  },
  title: {
    type: "text",
    default: "Lorem ipsum dolor sit amet consectetur adipiscing"
  },
  titleAlign: {
    type: "text",
    default: "center",
    options: ["left", "center", "right"],
    label: "Title Alignment"
  },
  event1: {
    type: "text",
    default: "",
    label: "Event 1 (override)"
  },
  event2: {
    type: "text",
    default: "",
    label: "Event 2 (override)"
  },
  event3: {
    type: "text",
    default: "",
    label: "Event 3 (override)"
  },
  event4: {
    type: "text",
    default: "",
    label: "Event 4 (override)"
  },
  event5: {
    type: "text",
    default: "",
    label: "Event 5 (override)"
  },
  event6: {
    type: "text",
    default: "",
    label: "Event 6 (override)"
  },
  textAlign: {
    type: "text",
    default: "center",
    options: ["left", "center", "right"],
    label: "Text Position"
  },
  gap: {
    type: "number",
    default: 650,
    min: 0,
    max: 1100,
    step: 10,
    slider: true,
    label: "Vertical Gap (px)"
  }
};

// export const presets = {
//   vertical: {
//     format: "vertical",
//     gap: 650
//   },
//   square: {
//     format: "square",
//     gap: 550
//   },
//   landscape: {
//     format: "landscape",
//     gap: 250
//   },
//   story: {
//     format: "story",
//     gap: 800
//   }
// };

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Event Layout",
  persistRandomOnExport: true,
  hidePresets: true,
  hideAutoRefresh: true,
  hideFunction: true
};
